
import { toast } from 'sonner';
import { useCallback } from 'react';

// Define the Position type to match sonner's requirements
type Position = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';

// Common toast configurations
const defaultConfig = {
  duration: 4000,
  position: 'top-right' as Position,
  className: 'app-toast'
};

// Possible toast types
type ToastType = 'success' | 'error' | 'info' | 'warning';

// Options for the toast notifications
interface NotifyOptions {
  description?: React.ReactNode;
  duration?: number;
  position?: Position;
  className?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

/**
 * Custom hook for consistent toast notifications throughout the app
 * @returns Object with methods for different types of notifications
 */
export function useNotify() {
  // Success notification
  const success = useCallback((message: string, options?: NotifyOptions) => {
    toast.success(message, {
      ...defaultConfig,
      ...options,
    });
  }, []);
  
  // Error notification
  const error = useCallback((message: string, options?: NotifyOptions) => {
    toast.error(message, {
      ...defaultConfig,
      ...options,
      duration: options?.duration || 5000, // Errors stay longer by default
    });
  }, []);
  
  // Info notification
  const info = useCallback((message: string, options?: NotifyOptions) => {
    toast.info(message, {
      ...defaultConfig,
      ...options,
    });
  }, []);
  
  // Warning notification
  const warning = useCallback((message: string, options?: NotifyOptions) => {
    toast.warning(message, {
      ...defaultConfig,
      ...options,
      duration: options?.duration || 4500, // Warnings stay a bit longer by default
    });
  }, []);
  
  // Custom notification with more control
  const custom = useCallback((
    message: string, 
    type: ToastType = 'info',
    options?: NotifyOptions
  ) => {
    switch (type) {
      case 'success':
        success(message, options);
        break;
      case 'error':
        error(message, options);
        break;
      case 'warning':
        warning(message, options);
        break;
      case 'info':
      default:
        info(message, options);
        break;
    }
  }, [success, error, warning, info]);
  
  // Dismiss all notifications
  const dismiss = useCallback(() => {
    toast.dismiss();
  }, []);
  
  return {
    success,
    error,
    info,
    warning,
    custom,
    dismiss
  };
}
